import { useEffect, useRef } from 'react';
import {
  type AdUnitConfiguration,
  SKYSCRAPER_LEFT_SLOT_ID,
  SKYSCRAPER_RIGHT_SLOT_ID,
  TOP_BANNER_SLOT_ID,
} from '@cx-web/feature-gam-advertising';

import { type Repub } from '@cx-modules/constants';

interface GoogleAdUnitProps {
  adUnitConfiguration: AdUnitConfiguration[];
  repub: Repub;
}

declare global {
  interface Window {
    googletag: any;
  }
}

/**
 * googletag `defineSlot`/`display` bootstrap for the page's full ad configuration. Renders nothing.
 *
 * This is deliberately split from rendering the slots' target `<div>`s: the page's ad placements sit
 * in different parts of the page, not bunched wherever this component happens to be mounted. Each
 * `create` slot's div is rendered independently by `<AdSlot placement="..." />` (see AdSlot.tsx),
 * wherever the layout needs it — `GoogleAdUnit` just needs to run once per `adUnitConfiguration`, with
 * the complete configuration, so every slot (including `inject` slots whose divs the layout/CSS
 * already owns) gets defined and displayed regardless of how many `<AdSlot>`s are mounted.
 *
 * Re-runs on every `adUnitConfiguration` change rather than once ever: this component is mounted once
 * in the persistent LayoutComponents/StandardLayout, above the swappable page-template content, so a
 * client-side navigation that changes which template renders (e.g. DynamicPageTemplate →
 * SportsPageHubTemplate) unmounts and remounts every ad target `<div>` (templates own them, not this
 * component) while GoogleAdUnit itself stays mounted. `adUnitConfiguration` is rebuilt fresh by the
 * loader on every navigation (its `adUnitCode` embeds the request path), so it's already a correct,
 * changing effect dependency — a one-shot guard here was the only defect. `setupRef` still protects
 * the handful of calls that must genuinely run exactly once per page lifetime (the `slotRenderEnded`
 * listener, `enableSingleRequest`, `enableServices`); `activeSlotsRef` lets each re-run `destroySlots()`
 * the previous run's slots first, GPT's own documented pattern for SPA navigation.
 */
const GoogleAdUnit = ({ adUnitConfiguration, repub }: GoogleAdUnitProps) => {
  const setupRef = useRef(false);
  const activeSlotsRef = useRef<any[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.googletag || !adUnitConfiguration?.length) return;

    window.googletag.cmd.push(() => {
      window.googletag.pubads().setTargeting('repub', repub);

      if (activeSlotsRef.current.length > 0) {
        window.googletag.destroySlots(activeSlotsRef.current);
        activeSlotsRef.current = [];
      }

      activeSlotsRef.current = adUnitConfiguration
        .map(({ adUnitCode, dimensions, targetNode, targeting }) => {
          const slot = window.googletag
            .defineSlot(adUnitCode, dimensions, targetNode)
            ?.addService(window.googletag.pubads());

          if (targeting?.position) {
            slot?.setTargeting('position', [targeting.position]);
          }

          return slot;
        })
        .filter(Boolean);

      if (!setupRef.current) {
        // Marks a slot's div once GPT has actually rendered a creative into it, so CSS can reveal
        // slots that reserve space ONLY when there's a real ad to show (e.g.
        // #apn_ad_slot_728_top_generic — see global.css) instead of displaying an empty placeholder
        // for a blocked request OR a genuine no-fill. `event.isEmpty` covers both cases.
        window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
          if (event.isEmpty) return;
          const slotElementId = event.slot.getSlotElementId();
          const slotEl = document.getElementById(slotElementId);
          slotEl?.setAttribute('data-ad-rendered', 'true');

          // The skyscraper rails' target div sits inside an outer wrapper (.ad-left-container/
          // .ad-right-container — see global.css) that independently reserves the 160px gutter width.
          // Flagging only the inner div left that wrapper always reserving its grid column regardless
          // of whether the ad was blocked, so it needs the same attribute.
          slotEl?.closest('.ad-left-container, .ad-right-container')?.setAttribute('data-ad-rendered', 'true');

          // Takeover skyscraper creatives (360x811/530x650/800x1000 — adUnitSetup.ts) exceed the
          // standard 600px max-height cap (global.css). GPT reports the ACTUAL delivered creative
          // size on event.size ([width, height]); when a skyscraper renders one taller than 600px,
          // flag the existing `data-takeovers` page attribute (global.css already has
          // `[data-takeovers='true'] #apn_ad_slot_left/right { max-height: none }` — previously
          // always inert because nothing ever set it) on the page's <main>, mirroring where this
          // attribute is read elsewhere in that stylesheet.
          const isSkyscraper = slotElementId === SKYSCRAPER_LEFT_SLOT_ID || slotElementId === SKYSCRAPER_RIGHT_SLOT_ID;
          const renderedHeight = Array.isArray(event.size) ? event.size[1] : undefined;
          if (isSkyscraper && typeof renderedHeight === 'number' && renderedHeight > 600) {
            slotEl?.closest('main')?.setAttribute('data-takeovers', 'true');
          }
        });

        // Deliberately NOT calling pubads().collapseEmptyDivs(): GPT already defaults to leaving an
        // unfilled slot's div alone (no explicit opt-in needed) rather than collapsing it — CSS
        // controls visibility for every slot via `data-ad-rendered` instead (see global.css), which
        // needs the div to still be there to attach the attribute to, not collapsed by GPT itself.
        window.googletag.pubads().enableSingleRequest();
        window.googletag.enableServices();
        setupRef.current = true;
      }

      adUnitConfiguration.forEach(({ targetNode }) => {
        // A template that structurally has no div for a given placement (e.g. SportsPageGridTemplate
        // has no right rail, so no #apn_ad_slot_MPU div exists) must not display() into it — GPT would
        // still send a phantom SRA request for a target that can never render.
        if (document.getElementById(targetNode)) {
          window.googletag.display(targetNode);
        }
      });
    });
  }, [adUnitConfiguration, repub]);

  // Google's own Publisher Console debug overlay (`?google_console` / `?google_console=1`) draws an
  // on-page marker for every ad slot GPT knows about, but cannot draw one for a hidden/zero-size
  // element. #apn_ad_slot_728_top_generic is `display: none` until a real ad renders (see global.css)
  // to avoid a layout shift for real visitors, so it never gets a marker while debugging via the
  // console unless forced. This forces it visible ONLY for that debug workflow — pure
  // `window.location.search` inspection, independent of googletag/the mock entirely, so it still runs
  // even when GPT never loads at all. Kept as a distinctly-named attribute (`data-debug-console-forced`,
  // not `data-ad-rendered`) so the two reveal reasons stay independently readable in the CSS.
  // `URLSearchParams#has` matches bare `?google_console` and `?google_console=1` alike (presence-only
  // check). Scoped to just this slot for now — every other slot has the same hidden-until-rendered
  // behaviour as of today, but extending this debug forcing to all of them is a separate follow-up,
  // not needed to fix the current layout-shift/reserved-space bug.
  //
  // Runs once per mount. This component stays mounted across client-side (SPA) route transitions (it
  // lives in the persistent LayoutComponents, above <Outlet>), so it will not react to the query
  // string changing without a full navigation — matching the confirmed QA workflow of loading a
  // `?google_console=1` URL directly, not toggling it mid-session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('google_console')) return;
    document.getElementById(TOP_BANNER_SLOT_ID)?.setAttribute('data-debug-console-forced', 'true');
  }, []);

  return null;
};

export default GoogleAdUnit;
